import { logout } from "@/utils/authentication";
import { createContext, useEffect, useState, use } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify"; // Make sure react-toastify is installed
import { useTranslation } from "react-i18next";

const SessionContext = createContext<{
  tokenExpiration: number | null;
  lastActivity: number;
  loggedInTime: number | null;
  sessionLogout: () => void;
  resetActivity: () => void;
    }>({
      tokenExpiration: null,
      lastActivity: 0,
      loggedInTime: null,
      sessionLogout: () => {},
      resetActivity: () => {},
    });

export const useSession = () => {
  return use(SessionContext);
};

export const SessionProvider = ({ children }: any) => {

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("toasts");

  // States
  const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [loggedInTime, setLoggedInTime] = useState<number | null>(null);
  const [showingWarning, setShowingWarning] = useState(false);

  useEffect(() => {
    // Use token and loggedIn from session to determine if user is logged in
    const token = sessionStorage.getItem("token");
    const storedLoggedInTime = sessionStorage.getItem("loggedIn");

    if (token) {
      try {
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        // Restore normal token expiration handling
        setTokenExpiration(tokenPayload.exp * 1000); // Convert to milliseconds

        if (storedLoggedInTime) {
          setLoggedInTime(Number(storedLoggedInTime));
          setLastActivity(Date.now());
        }
      } catch (error) {
        console.error("Error parsing token:", error);
        sessionLogout(); // Invalid token, log out
      }
    } else {
      setTokenExpiration(null);
      setLoggedInTime(null);
    }
  }, [location.pathname]);

  // Update last activity time when user interacts with the page
  useEffect(() => {
    if (loggedInTime) {
      // List of events to listen for to update last activity time
      const activityEvents = ["mousemove", "keydown", "scroll", "click", "touchstart"];

      const activityHandler = () => {
        setLastActivity(Date.now());

        // Reset warning flag and dismiss any existing warning toasts when activity is detected
        if (showingWarning) {
          setShowingWarning(false);
          // Dismiss all existing warning toasts
          toast.dismiss();
        }
      };

      // Add event listeners to events in the event list to update last activity time
      activityEvents.forEach((event) => {
        window.addEventListener(event, activityHandler);
      });

      // Remove event listeners when component unmounts
      return () => {
        activityEvents.forEach((event) => {
          window.removeEventListener(event, activityHandler);
        });
      };
    }
  }, [loggedInTime, showingWarning]);

  // Check for token expiration
  useEffect(() => {
    if (tokenExpiration) {
      const checkTokenExpiration = setInterval(() => {
        const currentTime = Date.now();
        const timeToExpiration = tokenExpiration - currentTime;

        // Show warning 2 minutes before expiration
        if (timeToExpiration <= 120000 && timeToExpiration > 0 && !showingWarning) {
          toast.warning(t("sessionExpiringWarning"), {
            autoClose: 10000,
            position: "top-right"
          });
          setShowingWarning(true);
        }

        if (currentTime >= tokenExpiration) {
          // Show the toast and wait before logging out
          toast.error(t("sessionExpired"), {
            autoClose: 5000,
            position: "top-center"
          });

          // Delay the actual logout to give the toast time to display
          setTimeout(() => {
            sessionLogout(); // Token has expired
          }, 1500);

          clearInterval(checkTokenExpiration);
        }
      }, 30000); // Check every 30 seconds

      return () => clearInterval(checkTokenExpiration);
    }
  }, [tokenExpiration, showingWarning, t]);

  useEffect(() => {
    if (loggedInTime) {
      const checkActivity = setInterval(() => {
        const elapsed = Date.now() - lastActivity;
        const twentyMinutes = 20 * 60 * 1000; // 20 minutes in milliseconds
        const eighteenMinutes = 18 * 60 * 1000; // 18 minutes for warning

        // Show warning after 18 minutes of inactivity
        if (elapsed >= eighteenMinutes && elapsed < twentyMinutes && !showingWarning) {
          toast.warning(t("inactivityWarning"), {
            autoClose: 10000,
            position: "top-right"
          });
          setShowingWarning(true);
        }

        if (elapsed >= twentyMinutes) {
          // Show the toast and wait before logging out
          toast.error(t("inactivityLogout"), {
            autoClose: 5000,
            position: "top-center"
          });

          // Delay the actual logout to give the toast time to display
          setTimeout(() => {
            sessionLogout(); // User has been inactive
          }, 1500);

          clearInterval(checkActivity);
        }
      }, 60000); // Check every minute

      return () => clearInterval(checkActivity);
    }
  }, [lastActivity, loggedInTime, showingWarning, t]);

  const sessionLogout = () => {
    logout(true);
    navigate("/login");
  };

  const resetActivity = () => {
    setLastActivity(Date.now());
    setShowingWarning(false);
    // Dismiss any existing warning toasts
    toast.dismiss();
  };

  return (
    <SessionContext
      value={{
        tokenExpiration,
        lastActivity,
        loggedInTime,
        sessionLogout,
        resetActivity
      }}
    >
      {children}
    </SessionContext>
  );
};

export { SessionContext };
